import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { paths } from '@/config/paths';
import {
  DATA_SOURCES,
  sourceSections,
  useAnalyticsModel,
  type DataSource,
} from '@/features/analytics';
import { syncedLabel } from '@/shared/lib';
import { Icon, type IconName } from '@/shared/ui';
import { useGetMetaQuery } from '../api/portalApi';
import type { ListEntity } from '../api/types';
import { navToggled, selectNavExpanded, type NavSection } from '../model/uiSlice';

interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  /** A list: its submenu holds its pages, ending with its views page. */
  list?: {
    entity: ListEntity;
    /** Dashboard page of the list, first in its submenu. */
    dashboard?: string;
    /** Analysis pages of the list, after its dashboard. */
    analyses?: { to: string; label: string }[];
    /**
     * Page with the list's views as tiles, last in its submenu. The menu lists no views itself
     * (nor New view): the page does, which keeps the menu short.
     */
    views: string;
  };
  /** Heading shown above this entry: it starts a group of the menu. */
  group?: string;
  /** Pages below this one, in a submenu that a chevron shows and hides. */
  pages?: { to: string; label: string }[];
}

/**
 * Report navigation as in the prototype, plus Totals (whole-data counts), the Growth reports and
 * the Data group (below); the rest of the data explorer is reached at /data.
 */
const NAV: NavEntry[] = [
  { to: '/overview', label: 'Overview', icon: 'overview' },
  { to: paths.trends, label: 'Trends', icon: 'trend' },
  { to: '/revenue', label: 'Revenue', icon: 'revenue' },
  {
    to: '/subscriptions',
    label: 'Subscriptions',
    icon: 'subscriptions',
    list: {
      entity: 'subscriptions',
      dashboard: paths.subscriptionsDashboard,
      analyses: [
        { to: paths.subscriptionLifecycle, label: 'Lifecycle' },
        { to: paths.subscriptionChurn, label: 'Churn' },
        { to: paths.subscriptionComparison, label: 'Comparison' },
        { to: paths.subscriptionReports, label: 'Reports' },
      ],
      views: paths.subscriptionViews,
    },
  },
  { to: '/renewals', label: 'Renewals', icon: 'calendar' },
  {
    to: '/offers',
    label: 'Product offers',
    icon: 'offers',
    pages: [
      { to: paths.offerPrices('standard'), label: 'Standard' },
      { to: paths.offerPrices('custom'), label: 'Custom' },
      { to: paths.offerPrices('archived'), label: 'Archived' },
      { to: paths.discounts, label: 'Discounts' },
      { to: paths.coupons, label: 'Coupons' },
    ],
  },
  {
    to: '/users',
    label: 'Users',
    icon: 'users',
    list: { entity: 'users', views: paths.userViews },
  },
  {
    to: '/apps',
    label: 'Apps',
    icon: 'apps',
    list: { entity: 'apps', dashboard: paths.appsDashboard, views: paths.appViews },
  },
  { to: '/cohorts', label: 'Cohorts', icon: 'cohorts' },
  { to: '/totals', label: 'Totals', icon: 'graph' },
  { to: '/conversion', label: 'Conversion', icon: 'funnel', group: 'Growth' },
  { to: '/trials', label: 'Trials', icon: 'sparkle' },
  { to: '/markets', label: 'Markets', icon: 'globe' },
  {
    to: '/leads',
    label: 'Leads',
    icon: 'target',
    list: { entity: 'leads', views: paths.leadViews },
  },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const { data: meta } = useGetMetaQuery(undefined);
  const section = `/${pathname.split('/')[1] ?? ''}`;

  return (
    <aside className="sidebar">
      <Link className="brand" to="/overview" aria-label="Open as App KPI portal, overview">
        <img className="brand__logo" src="/assets/logo-primary-negative.svg" alt="Open as App" />
      </Link>
      <nav className="nav" aria-label="Reports">
        {NAV.map((entry) => (
          <Fragment key={entry.to}>
            {entry.group && <span className="nav__group">{entry.group}</span>}
            {entry.list ? (
              <ListNavItem
                entry={entry}
                list={entry.list}
                current={section === entry.to}
                pathname={pathname}
              />
            ) : entry.pages ? (
              <PagesNavItem entry={entry} pages={entry.pages} pathname={pathname} />
            ) : (
              <Link
                className="nav__link"
                to={entry.to}
                aria-current={section === entry.to ? 'page' : undefined}
              >
                <Icon name={entry.icon} />
                {entry.label}
              </Link>
            )}
          </Fragment>
        ))}
        <DataNavGroup pathname={pathname} />
      </nav>
      <div className="sidebar__foot">
        <Link
          className="nav__link sidebar__help"
          to="/help"
          aria-current={section === '/help' ? 'page' : undefined}
        >
          <Icon name="help" />
          Help
        </Link>
        <div className="sync">
          <span className="sync__status">Synced {syncedLabel(meta?.stripeSyncedAt)}</span>
        </div>
      </div>
    </aside>
  );
}

interface ListNavItemProps {
  entry: NavEntry;
  list: NonNullable<NavEntry['list']>;
  /** The list, one of its pages or one of its record forms is open. */
  current: boolean;
  pathname: string;
}

/** A list with its pages: dashboard, analyses and the views page, which opens the views. */
function ListNavItem({ entry, list, current, pathname }: ListNavItemProps) {
  const dispatch = useAppDispatch();
  const expandedState = useAppSelector(selectNavExpanded);
  const expanded = expandedState[list.entity] ?? current;
  const subId = `pages-nav-${list.entity}`;
  const what = `${entry.label} pages`;
  const pages = [
    ...(list.dashboard ? [{ to: list.dashboard, label: 'Dashboard' }] : []),
    ...(list.analyses ?? []),
    { to: list.views, label: 'Data views' },
  ];
  // The list page shows one of the views, so Data views stays marked there; a page with pages
  // below it (Reports and its reports) stays marked on them.
  const pageCurrent = (to: string) =>
    pathname === to ||
    pathname.startsWith(`${to}/`) ||
    (to === list.views && pathname === entry.to);

  return (
    <>
      <div className="nav__item">
        <Link className="nav__link" to={entry.to} aria-current={current ? 'page' : undefined}>
          <Icon name={entry.icon} />
          {entry.label}
        </Link>
        <button
          type="button"
          className="nav__toggle"
          aria-expanded={expanded}
          aria-controls={subId}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${what}`}
          onClick={() => dispatch(navToggled({ section: list.entity, expanded: !expanded }))}
        >
          <Icon name="chevron" size="xs" />
        </button>
      </div>
      <div className="nav__sub" id={subId} role="group" aria-label={what} hidden={!expanded}>
        <ul className="nav__views">
          {pages.map((page) => (
            <li key={page.to}>
              <Link
                className="nav__view"
                to={page.to}
                aria-current={pageCurrent(page.to) ? 'page' : undefined}
              >
                <span className="nav__view-name">{page.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/**
 * A report with subpages (Product offers): a link to its own page and a toggle that shows the
 * subpages. The submenu stays open while the report or one of its subpages is open.
 */
function PagesNavItem({
  entry,
  pages,
  pathname,
}: {
  entry: NavEntry;
  pages: { to: string; label: string }[];
  pathname: string;
}) {
  const dispatch = useAppDispatch();
  const expandedState = useAppSelector(selectNavExpanded);
  const section = entry.to.slice(1) as NavSection;
  const expanded = expandedState[section] ?? isWithin(pathname, entry.to);
  const subId = `pages-nav-${section}`;

  return (
    <>
      <div className="nav__item">
        <Link
          className="nav__link"
          to={entry.to}
          aria-current={pathname === entry.to ? 'page' : undefined}
        >
          <Icon name={entry.icon} />
          {entry.label}
        </Link>
        <button
          type="button"
          className="nav__toggle"
          aria-expanded={expanded}
          aria-controls={subId}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${entry.label} pages`}
          onClick={() => dispatch(navToggled({ section, expanded: !expanded }))}
        >
          <Icon name="chevron" size="xs" />
        </button>
      </div>
      <div
        className="nav__sub"
        id={subId}
        role="group"
        aria-label={`${entry.label} pages`}
        hidden={!expanded}
      >
        <ul className="nav__views">
          {pages.map((page) => (
            <li key={page.to}>
              <Link
                className="nav__view"
                to={page.to}
                aria-current={isWithin(pathname, page.to) ? 'page' : undefined}
              >
                <span className="nav__view-name">{page.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** Whether `pathname` is `base` or a page below it. */
const isWithin = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

const schemaPath = (schema: string) => paths.analytics.schema(schema);

/** A page of one of the schemas is open. */
const isWithinSchemas = (pathname: string, schemas: string[]) =>
  schemas.some((schema) => isWithin(pathname, schemaPath(schema)));

interface SectionToggleProps {
  section: NavSection;
  expanded: boolean;
  /** ID of the element it shows and hides. */
  controls: string;
  className: string;
  label: string;
  icon?: IconName;
}

/** A menu heading or row that shows and hides its section; the menu remembers the state. */
function SectionToggle({
  section,
  expanded,
  controls,
  className,
  label,
  icon,
}: SectionToggleProps) {
  const dispatch = useAppDispatch();
  return (
    <button
      type="button"
      className={className}
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={() => dispatch(navToggled({ section, expanded: !expanded }))}
    >
      {icon && <Icon name={icon} />}
      {label}
      <Icon name="chevron" size="xs" />
    </button>
  );
}

/** A source's overview or one of its tables is open. */
const isWithinSource = (pathname: string, source: DataSource) =>
  pathname === paths.analytics.source(source.id) || isWithinSchemas(pathname, source.schemas);

/**
 * The Data group, a heading like Growth, with the source systems of the data explorer (OAA,
 * Stripe, BI) and their tables. The group and each source stay collapsed until opened, or while
 * the source's overview or one of its tables is open.
 */
function DataNavGroup({ pathname }: { pathname: string }) {
  const expandedState = useAppSelector(selectNavExpanded);
  const current = DATA_SOURCES.some((source) => isWithinSource(pathname, source));
  const expanded = expandedState.data ?? current;

  return (
    <>
      <SectionToggle
        section="data"
        expanded={expanded}
        controls="data-nav"
        className="nav__group nav__group--toggle"
        label="Data"
      />
      <div className="nav__section" id="data-nav" role="group" aria-label="Data" hidden={!expanded}>
        {DATA_SOURCES.map((source) => (
          <DataSourceItem key={source.id} source={source} pathname={pathname} />
        ))}
      </div>
    </>
  );
}

/**
 * One source system: a link to its overview and a toggle that shows its tables, as the lists show
 * their views. The schema is requested only while the tables are shown.
 */
function DataSourceItem({ source, pathname }: { source: DataSource; pathname: string }) {
  const dispatch = useAppDispatch();
  const expandedState = useAppSelector(selectNavExpanded);
  const expanded = expandedState[source.id] ?? isWithinSource(pathname, source);
  const subId = `data-nav-${source.id}`;
  const to = paths.analytics.source(source.id);

  return (
    <>
      <div className="nav__item">
        <Link className="nav__link" to={to} aria-current={pathname === to ? 'page' : undefined}>
          <Icon name="database" />
          {source.label}
        </Link>
        <button
          type="button"
          className="nav__toggle"
          aria-expanded={expanded}
          aria-controls={subId}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${source.label} tables`}
          onClick={() => dispatch(navToggled({ section: source.id, expanded: !expanded }))}
        >
          <Icon name="chevron" size="xs" />
        </button>
      </div>
      <div
        className="nav__sub"
        id={subId}
        role="group"
        aria-label={`${source.label} tables`}
        hidden={!expanded}
      >
        {expanded && <DataTableLinks source={source} pathname={pathname} />}
      </div>
    </>
  );
}

function DataTableLinks({ source, pathname }: { source: DataSource; pathname: string }) {
  const { model, error } = useAnalyticsModel();
  if (!model) {
    return <span className="nav__view">{error ? 'Tables could not be loaded' : 'Loading…'}</span>;
  }
  // A schema with several subgroups (dbo: Production, Usage) lists each under its own heading.
  const sections = sourceSections(model, source);

  return (
    <>
      {sections.map((section) => (
        <Fragment key={section.key}>
          {sections.length > 1 && <span className="nav__group">{section.label}</span>}
          <ul className="nav__views">
            {section.objects.map((object) => {
              // A table has one page: its overview, with its records in the Data tab.
              const open = isWithin(pathname, paths.analytics.object(object.key));
              return (
                <li key={object.key}>
                  <Link
                    className="nav__view"
                    to={paths.analytics.object(object.key)}
                    aria-current={open ? 'page' : undefined}
                  >
                    <span className="nav__view-name">{object.title}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Fragment>
      ))}
    </>
  );
}
